// © 2025 Camp Promethean, LLC. All Rights Reserved.

import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';

interface UseIsAdminResult {
  isAdmin: boolean;
  loading: boolean;
  error: Error | null;
}

/**
 * Hook to check if the current user has admin permissions
 * Uses the 'role' field in the profile from AuthContext
 */
export function useIsAdmin(): UseIsAdminResult {
  const { profile, loading: authLoading } = useAuth();
  const [isAdmin, setIsAdmin] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    function checkAdminStatus() {
      if (authLoading) {
        return;
      }

      try {
        // If no profile, user is not an admin
        if (!profile) {
          setIsAdmin(false);
          setError(null);
          return;
        }

        // Check if role is admin
        setIsAdmin(profile.role === 'admin');
        setError(null);
      } catch (err) {
        console.error('Error checking admin status:', err);
        setError(err instanceof Error ? err : new Error('Unknown error checking admin status'));
        setIsAdmin(false);
      } finally {
        setLoading(false);
      }
    }

    checkAdminStatus();
  }, [profile, authLoading]);

  return { isAdmin, loading, error };
}

export default useIsAdmin; 